import { flatten } from 'shared/util'
import {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react'
import { useSearchParams } from 'react-router-dom'

import { useListDpcDisease, useListMdc, useListMdcResult } from 'interface/dpc'
import { SisetusDpcResult } from 'interface/dpc/types'

import { TextMatch } from 'shared/text'
import { SelectedDpc } from '../types'

// note: ランダムに変わっちゃうと使い勝手が良くないので...
//import { generateColorPallete } from 'shared/generateColorPallete'
const colorPalette = [
  '#ff0000',
  '#00ff00',
  '#0000ff',
  '#ff00ff',
  '#00ffff',
  '#ffff00',
  '#ff8800',
  '#ff0088',
  '#0088ff',
  '#8800ff',
  '#00ff88',
  '#ff0055',
  '#00ff55',
  '#ff0808',
  '#5500ff',
  '#ffaa00',
  '#ff5500',
  '#88ff00',
  '#55ff00',
  '#009cff',
]

export const useDpcResults = () => {
  const [params, setParams] = useSearchParams()

  const [selectedMdcCode, setSelectedMdcCode] = useState<string | null>(
    params.get('mdc')
  )
  const [selectedDpcCodes, setSelectedDpcCodes] = useState<string[]>(
    params.get('dpc')?.split('.') ?? []
  )
  const [block, setBlock] = useState<string>(
    params.get('block') ?? 'all'
  )
  const [prefectures, setPrefectures] = useState<string[]>(
    params.get('pref')?.split('.').filter(p => p !== '') || []
  )

  const inputTimer = useRef<NodeJS.Timeout | null>(null)
  const [filterWord, setFilterWordCore] = useState<string>('')

  const [tabValue, setTabValue] = useState<'list' | 'map'>('list')
  const mdcMasterState = useListMdc()
  const dpcMasterState = useListDpcDisease(selectedMdcCode)
  const dpcResultsState = useListMdcResult(selectedMdcCode, prefectures, undefined)
  
  const [processing, setProcessing] = useState<boolean>(false)
  const [results, setResults] = useState<SisetusDpcResult[] | null>(null)

  const mdcList = useMemo(
    () => {
      const mdcList = mdcMasterState.data?.list ?? []
      mdcList.sort((a, b) => a.mdcCode.localeCompare(b.mdcCode))
      return mdcList
    },
    [mdcMasterState.data]
  )

  const dpcList = useMemo(
    () => {
      const dpcList = dpcMasterState.data?.list ?? []
      dpcList.sort((a, b) => a.dpcCode.localeCompare(b.dpcCode))
      return dpcList
    },
    [dpcMasterState.data]
  )

  const dpcResults = useMemo(
    () => {
      return dpcResultsState.data?.list ?? []
    },
    [dpcResultsState.data]
  )

  const selectMdcCode = useCallback(
    (mdcCode: string | null) => {
      setSelectedMdcCode(mdcCode)
      setSelectedDpcCodes([])
    },
    [setSelectedMdcCode, setSelectedDpcCodes]
  )
  const addSelectedDpcCode = useCallback(
    (dpcCode: string) => {
      if (selectedDpcCodes.indexOf(dpcCode) >= 0) return
      const dpcCodes = [...selectedDpcCodes].concat([dpcCode])
      setSelectedDpcCodes(dpcCodes)
    },
    [selectedDpcCodes, setSelectedDpcCodes]
  )
  const removeSelectedDpcCode = useCallback(
    (dpcCode: string) => {
      const dpcCodes = selectedDpcCodes.flatMap((exist) =>
        exist === dpcCode ? [] : exist
      )
      setSelectedDpcCodes(dpcCodes)
    },
    [selectedDpcCodes, setSelectedDpcCodes]
  )
  const selectDpcCode = useCallback(
    (dpcCode: string, set: boolean) => {
      if (set) {
        addSelectedDpcCode(dpcCode)
      } else {
        removeSelectedDpcCode(dpcCode)
      }
    },
    [addSelectedDpcCode, removeSelectedDpcCode]
  )
  const clearSelectedDpcCode = useCallback(
    () => {
      setSelectedDpcCodes([])
    },
    [setSelectedDpcCodes]
  )

  useEffect(
    () => setParams(prev => {
      if (selectedMdcCode) {
        prev.set('mdc', selectedMdcCode)
      } else {
        prev.delete('mdc')
      }
      if (selectedDpcCodes.length > 0) {
        prev.set('dpc', selectedDpcCodes.join('.'))
      } else {
        prev.delete('dpc')
      }
      return prev
    }),
    [setParams, selectedMdcCode, selectedDpcCodes]
  )
  useEffect(
    () => setParams(prev => {
      if (block !== 'all') {
        prev.set('block', block)
      } else {
        prev.delete('block')
      }
      return prev
    }),
    [setParams, block]
  )
  useEffect(
    () => setParams(prev => {
      if (prefectures.length > 0) {
        prev.set('pref', prefectures.join('.'))
      } else {
        prev.delete('pref')
      }
      return prev
    }),
    [setParams, prefectures]
  )

  const setFilterWord = useCallback(
    (word: string) => {
      if (inputTimer.current) clearTimeout(inputTimer.current)
      inputTimer.current = setTimeout(() => {
        setFilterWordCore(word)
        inputTimer.current = null
      }, 100)
    },
    [setFilterWordCore]
  )

  const selectedDpcList: SelectedDpc[] = useMemo(
    () => {
      const dpcMaster = dpcMasterState.data
      if (!dpcMaster) return []
      //const colorPalette = generateColorPallete(selectedDpcCodes.length)
      return selectedDpcCodes.map((dpcCode, index) => {
        const diseaseName = dpcMaster.list.find(
          dpc => dpc.dpcCode === dpcCode
        )?.diseaseName
        const color = colorPalette[index % colorPalette.length]
        return { dpcCode, diseaseName, color }
      })
    },
    [selectedDpcCodes, dpcMasterState]
  )

  const calcTotalCase = useCallback(
    (result: SisetusDpcResult, dpcCode: string) => Math.max(
      result.surgery[dpcCode]?.totalCases ?? 0,
      result.treatment1[dpcCode]?.totalCases ?? 0,
      result.treatment2[dpcCode]?.totalCases ?? 0
    ),
    []
  )

  const filteredList = useMemo(
    () => {
      if (
        dpcResultsState.loading ||
        dpcResultsState.error ||
        !dpcResultsState.data
      ) return null

      const word = filterWord.trim()
      if (word === '') return dpcResultsState.data.list

      const match = new TextMatch(word)
      return dpcResultsState.data.list.filter(
        result => {
          if (match && !match.match(
            result.sisetu.sisetuCode,
            result.sisetu.sisetuName,
            result.sisetu.sisetuNameKana,
            result.sisetu.juusyo,
            result.sisetu.tel
          )) return false
          return true
        }
      )
    },
    [filterWord, dpcResultsState]
  )
  useEffect(
    () => {
      setProcessing(true)
      const timer = setTimeout(() => {
        // 選択しているDPCコードが有るもののみに絞る
        const results = filteredList?.filter(sisetu => selectedDpcCodes.some(
          dpcCode => calcTotalCase(sisetu, dpcCode) > 0
        ))
        results?.sort((a, b) => {
          for (const dpcCode of selectedDpcCodes) {
            const valueA = calcTotalCase(a, dpcCode)
            const valueB = calcTotalCase(b, dpcCode)
            if (valueA !== valueB) return valueB - valueA
          }
          return 0
        })
        setResults(results ?? null)
        setProcessing(false)
      })
      return () => clearTimeout(timer)
    },
    [filteredList, selectedDpcCodes, calcTotalCase, setResults]
  )
  return useMemo(
    () => ({
      loading: (
        mdcMasterState.loading ||
        dpcMasterState.loading ||
        dpcResultsState.loading ||
        processing
      ),
      errors: flatten([
        mdcMasterState.error,
        dpcMasterState.error,
        dpcResultsState.error,
      ]),
      mdcList,
      dpcList,
      dpcResults,

      tabValue, setTabValue,
      selectedMdcCode, selectMdcCode,
      selectedDpcCodes, selectDpcCode,
      selectedDpcList,
      clearSelectedDpcCode,
      block, setBlock,
      prefectures, setPrefectures,
      filterWord, setFilterWord,
      calcTotalCase,
      filteredList,
      results
    }),
    [
      mdcMasterState, mdcList,
      dpcMasterState, dpcList,
      dpcResultsState, dpcResults,
      processing,
      tabValue, setTabValue,
      selectedMdcCode, selectMdcCode,
      selectedDpcCodes, selectDpcCode,
      selectedDpcList,
      clearSelectedDpcCode,
      block, setBlock,
      prefectures, setPrefectures,
      filterWord, setFilterWord,
      calcTotalCase,
      filteredList,
      results
    ]
  )
}
